"use client";

/**
 * Public signup-counts provider.
 *
 * Prefetches per-slot registration counts for the WHOLE event once, so the activity signup modal —
 * opened from the Activities section, the inline Schedule, or the focused `?view=schedule` page —
 * renders capacity / waitlist state instantly instead of fetching counts when it opens.
 *
 * Counts are the only dynamic, event-wide value those surfaces previously fetched on click. They're
 * re-fetched whenever `registrationsVersion` bumps (a signup/cancel anywhere on the page) so the
 * numbers stay fresh, and a `refresh()` is exposed for callers that mutate outside that signal.
 */

import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useBuilderContext } from "../../_hooks/useBuilderContext";
import { getSlotRegistrationCountsForEvent, type SlotRegistrationCounts } from "../../_logic/actions/signup-actions";
import { usePublicViewContext } from "./PublicViewContext";

/** Shape of the shared, prefetched per-slot registration counts. */
export interface PublicSignupCountsData {
  /** slotId -> registration counts, prefetched event-wide. Empty until the first fetch resolves. */
  slotCounts: Record<number, SlotRegistrationCounts>;
  /** Re-fetch counts on demand. */
  refresh: () => void;
}

const PublicSignupCountsContext = createContext<PublicSignupCountsData | null>(null);

/**
 * Read the shared, prefetched per-slot registration counts.
 *
 * @returns The context value, or null when called outside the provider (e.g. builder mode).
 */
export function usePublicSignupCounts(): PublicSignupCountsData | null {
  return useContext(PublicSignupCountsContext);
}

interface PublicSignupCountsProviderProps {
  /** When false, skips the fetch entirely (page has neither activities nor a schedule). */
  enabled: boolean;
  /**
   * Server-prefetched per-slot counts. When provided, seeds state and skips the mount fetch (the
   * value already reflects load time); later signup/cancel bumps still re-fetch. Undefined in the
   * builder / when not prefetched, so the mount fetch runs as before.
   */
  initialSlotCounts?: Record<number, SlotRegistrationCounts>;
  children: React.ReactNode;
}

/**
 * Provides prefetched per-slot registration counts to its subtree. Mount inside the public
 * BuilderContext + PublicViewContext so it can read `eventId` and `registrationsVersion`.
 *
 * @param props - See PublicSignupCountsProviderProps.
 * @returns The context provider wrapping `children`.
 */
export function PublicSignupCountsProvider({
  enabled,
  initialSlotCounts,
  children,
}: PublicSignupCountsProviderProps): React.ReactElement {
  // Hooks
  const { eventId } = useBuilderContext();
  const { registrationsVersion } = usePublicViewContext();

  // States — seeded from the server prefetch when present.
  const [slotCounts, setSlotCounts] = useState<Record<number, SlotRegistrationCounts>>(() => initialSlotCounts ?? {});
  // When seeded, the mount fetch is redundant (state already reflects load time) — skip it once, but
  // still re-fetch on later registrationsVersion bumps.
  const skipMountFetchRef = useRef(initialSlotCounts != null);

  // Handlers
  const load = useCallback(
    (signal: { cancelled: boolean }) => {
      if (!enabled) return;
      getSlotRegistrationCountsForEvent(eventId)
        .then((counts) => {
          if (!signal.cancelled) setSlotCounts(counts);
        })
        .catch((error) => {
          if (!signal.cancelled) console.error("PublicSignupCountsProvider: failed to load slot counts", error);
        });
    },
    [enabled, eventId]
  );

  const refresh = useCallback(() => {
    const signal = { cancelled: false };
    load(signal);
  }, [load]);

  // Effects — prefetch on mount (unless server-seeded) and re-fetch whenever a signup/cancel bumps
  // the version.
  useEffect(() => {
    if (skipMountFetchRef.current) {
      skipMountFetchRef.current = false;
      return;
    }
    const signal = { cancelled: false };
    load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load, registrationsVersion]);

  // Derived values
  const value = useMemo<PublicSignupCountsData>(() => ({ slotCounts, refresh }), [slotCounts, refresh]);

  // Render
  return <PublicSignupCountsContext.Provider value={value}>{children}</PublicSignupCountsContext.Provider>;
}
